import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  Bug,
  Lightbulb,
  Palette,
  HelpCircle,
  Clock,
  User,
  Mail,
  Calendar,
  Paperclip,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';

const TYPE_ICONS = {
  bug: Bug,
  feature: Lightbulb,
  ux: Palette,
  general: HelpCircle,
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'warning' },
  { value: 'reviewed', label: 'Reviewed', color: 'primary' },
  { value: 'in_progress', label: 'In Progress', color: 'primary' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
];

export default function FeedbackDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [status, setStatus] = useState('new');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchFeedback();
  }, [id]);

  const fetchFeedback = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getFeedbackItem(id);
      setFeedback(response.feedback);
      setAdminNotes(response.feedback.adminNotes || '');
      setStatus(response.feedback.status || 'new');
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await adminApi.updateFeedback(id, { status, adminNotes });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update feedback:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'bug':
        return 'bg-red-100 text-red-600';
      case 'feature':
        return 'bg-green-100 text-green-600';
      case 'ux':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-blue-100 text-blue-600';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-48"></div>
          <div className="card p-6 space-y-4">
            <div className="h-6 bg-bg-secondary rounded w-3/4"></div>
            <div className="h-4 bg-bg-secondary rounded w-1/2"></div>
            <div className="h-32 bg-bg-secondary rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="card p-12 text-center">
          <AlertCircle className="w-12 h-12 text-danger mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Feedback Not Found
          </h2>
          <p className="text-text-muted mb-4">
            The feedback you're looking for doesn't exist or has been deleted.
          </p>
          <button onClick={() => navigate('/feedback')} className="btn-primary">
            Back to Feedback List
          </button>
        </div>
      </div>
    );
  }

  const TypeIcon = TYPE_ICONS[feedback.feedbackType] || MessageSquare;

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/feedback')}
          className="p-2 rounded-lg hover:bg-bg-secondary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-text-primary capitalize">
            {feedback.feedbackType || 'General'} Feedback
          </h1>
          <p className="text-text-secondary">
            Submitted {formatDate(feedback.createdAt)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Feedback Content */}
          <div className="card p-6">
            <div className="flex items-start gap-4 mb-6">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getTypeColor(
                  feedback.feedbackType
                )}`}
              >
                <TypeIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-medium text-text-primary capitalize">
                    {feedback.feedbackType || 'General'} Feedback
                  </span>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= (feedback.rating || 0)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-200'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-text-muted ml-1">
                      ({feedback.rating || 0}/5)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="font-medium text-text-primary mb-2">Message</h3>
            <p className="text-text-secondary whitespace-pre-wrap">
              {feedback.message}
            </p>

            {/* Attachments */}
            {feedback.attachments?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="font-medium text-text-primary mb-3 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments ({feedback.attachments.length})
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {feedback.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-bg-secondary rounded-lg hover:bg-bg-primary transition-colors"
                    >
                      {attachment.type?.startsWith('image/') ? (
                        <img
                          src={attachment.url}
                          alt={attachment.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                          <Paperclip className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <span className="text-sm text-text-primary truncate flex-1">
                        {attachment.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Admin Notes */}
          <div className="card p-6">
            <h3 className="font-medium text-text-primary mb-3">Admin Notes</h3>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add internal notes about this feedback..."
              className="input min-h-[120px] resize-y"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="card p-6">
            <h3 className="font-medium text-text-primary mb-3">Status</h3>
            <div className="space-y-2">
              {STATUS_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    status === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-bg-secondary'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={status === option.value}
                    onChange={(e) => setStatus(e.target.value)}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      status === option.value
                        ? 'border-primary'
                        : 'border-gray-300'
                    }`}
                  >
                    {status === option.value && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <span
                    className={
                      status === option.value
                        ? 'text-primary font-medium'
                        : 'text-text-secondary'
                    }
                  >
                    {option.label}
                  </span>
                </label>
              ))}
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary w-full mt-4"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : saveSuccess ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Saved!
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>

          {/* User Info */}
          <div className="card p-6">
            <h3 className="font-medium text-text-primary mb-4">
              Submitted By
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-text-primary">
                    {feedback.userName || 'Anonymous'}
                  </p>
                  <p className="text-sm text-text-muted">User</p>
                </div>
              </div>

              {feedback.userEmail && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-text-muted" />
                  <Link
                    to={`/settings?tab=compose&to=${encodeURIComponent(feedback.userEmail)}&subject=${encodeURIComponent(`Re: Your ${feedback.feedbackType || 'General'} Feedback`)}`}
                    className="text-primary hover:underline"
                  >
                    {feedback.userEmail}
                  </Link>
                </div>
              )}

              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-text-muted" />
                <span className="text-text-secondary">
                  {formatDate(feedback.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Device Info */}
          {feedback.deviceInfo && (
            <div className="card p-6">
              <h3 className="font-medium text-text-primary mb-4">
                Device Info
              </h3>
              <div className="text-sm space-y-2 text-text-secondary">
                {feedback.deviceInfo.browser && (
                  <p>Browser: {feedback.deviceInfo.browser}</p>
                )}
                {feedback.deviceInfo.os && (
                  <p>OS: {feedback.deviceInfo.os}</p>
                )}
                {feedback.deviceInfo.device && (
                  <p>Device: {feedback.deviceInfo.device}</p>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="card p-6">
            <h3 className="font-medium text-text-primary mb-4">Timeline</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    Submitted
                  </p>
                  <p className="text-xs text-text-muted">
                    {formatDate(feedback.createdAt)}
                  </p>
                </div>
              </div>

              {feedback.reviewedAt && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Reviewed
                    </p>
                    <p className="text-xs text-text-muted">
                      {formatDate(feedback.reviewedAt)}
                    </p>
                  </div>
                </div>
              )}

              {feedback.resolvedAt && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-success" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Resolved
                    </p>
                    <p className="text-xs text-text-muted">
                      {formatDate(feedback.resolvedAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
